import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { toast } from '@/shared/ui/feedback';
import { Icon } from '@/shared/ui/icon';
import { Input } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn/popover';
import { Switch } from '@/shared/ui/switch';

import { downloadBlob } from '../model/download';
import type { ListColumnDef, ListExportColumn } from '../model/types';
import { LIST_EXPORT_MAX_ROWS, type ListPageController } from '../model/useListPageController';

const NUMBER_FORMAT = new Intl.NumberFormat('cs-CZ');

/** Menu Řadit — všechny sloupce registru; klik na aktivní přepne směr (anatomie 02 §4). */
function SortMenu<TItem>({
  controller,
  columns,
}: {
  controller: ListPageController<TItem>;
  columns: ListColumnDef<TItem>[];
}) {
  const { t } = useTranslation('listPage');
  const sortable = columns.filter((c) => c.sortable !== false);
  const current = sortable.find((c) => (c.sortKey ?? c.key) === controller.sort.column);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="phxlist-btn"
          title={
            current
              ? t('toolbar.sortTitle', {
                  column: current.label,
                  direction: controller.sort.desc ? t('toolbar.sortDesc') : t('toolbar.sortAsc'),
                })
              : t('toolbar.sort')
          }
        >
          <Icon name="sort" size={13} />
          {t('toolbar.sortLabel', { column: current?.label ?? '—' })}
          {current ? (
            <span className="text-phx-primary inline-flex">
              <Icon name={controller.sort.desc ? 'chevdown' : 'chevup'} size={12} />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={5} className="p-phx-2 w-[210px]">
        <div className="phxlist-caps text-phx-text-muted px-phx-3 py-phx-2">
          {t('toolbar.sortBy')}
        </div>
        <div className="flex max-h-[300px] flex-col gap-px overflow-auto">
          {sortable.map((column) => {
            const sortKey = column.sortKey ?? column.key;
            const active = controller.sort.column === sortKey;
            return (
              <button
                key={column.key}
                type="button"
                className="phxlist-menuitem"
                data-active={active || undefined}
                onClick={() => controller.sortBy(sortKey)}
              >
                <span className="min-w-0 flex-1 truncate text-left">{column.label}</span>
                {active ? (
                  <span className="text-phx-primary inline-flex">
                    <Icon name={controller.sort.desc ? 'chevdown' : 'chevup'} size={12} />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Popover Sloupce (anatomie 02 §5) — šířky Auto/Ruční, hledání, hromadný řádek, DnD pořadí, špendlíky. */
function ColumnsPopover<TItem>({
  controller,
  columns,
}: {
  controller: ListPageController<TItem>;
  columns: ListColumnDef<TItem>[];
}) {
  const { t } = useTranslation('listPage');
  const [query, setQuery] = useState('');
  const [dragKey, setDragKey] = useState<string | null>(null);

  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const states = controller.columnStates;
  const normalized = query.trim().toLowerCase();
  const shown = normalized
    ? states.filter((s) => byKey.get(s.key)?.label.toLowerCase().includes(normalized))
    : states;

  const allShown = states.every((s) => s.visible);
  const someShown = states.some((s) => s.visible);
  const shownCount = states.filter((s) => s.visible).length;

  const toggleAll = () =>
    controller.setColumnStates(states.map((s) => (s.pinned ? s : { ...s, visible: !allShown })));

  const dragTo = (overKey: string) => {
    if (!dragKey || dragKey === overKey) {
      return;
    }
    const from = states.findIndex((s) => s.key === dragKey);
    const to = states.findIndex((s) => s.key === overKey);
    if (from < 0 || to < 0) {
      return;
    }
    const next = states.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    controller.setColumnStates(next);
  };

  return (
    <Popover
      onOpenChange={() => {
        setQuery('');
        setDragKey(null);
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className="phxlist-btn">
          <Icon name="list" size={13} />
          {t('toolbar.columns')}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={5} className="p-phx-2 w-[272px]">
        <div className="gap-phx-3 px-phx-1 flex items-center">
          <span className="phxlist-caps text-phx-text-muted">{t('columns.widthMode')}</span>
          <div className="phxlist-seg ml-auto">
            <button
              type="button"
              className="phxlist-seg-item"
              data-active={controller.widthMode === 'auto' || undefined}
              onClick={() => controller.setWidthMode('auto')}
            >
              {t('columns.widthAuto')}
            </button>
            <button
              type="button"
              className="phxlist-seg-item"
              data-active={controller.widthMode === 'manual' || undefined}
              onClick={() => controller.setWidthMode('manual')}
            >
              {t('columns.widthManual')}
            </button>
          </div>
        </div>
        <div className="my-phx-3">
          <Input
            size="sm"
            autoFocus
            leadingIcon="search"
            placeholder={t('columns.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="phxlist-colpick-all">
          <span className="w-phx-5" />
          <Checkbox
            checked={allShown}
            indeterminate={someShown && !allShown}
            onCheckedChange={toggleAll}
            label={allShown ? t('columns.hideAll') : t('columns.showAll')}
          />
          <span className="text-phx-xs text-phx-text-muted ml-auto tabular-nums">
            {t('columns.count', { shown: shownCount, total: states.length })}
          </span>
        </div>
        <div className="max-h-[300px] overflow-auto" onDragOver={(e) => e.preventDefault()}>
          {shown.map((state) => {
            const column = byKey.get(state.key);
            if (!column) {
              return null;
            }
            const dragDisabled = normalized.length > 0;
            return (
              <div
                key={state.key}
                className="phxlist-colpick-row"
                data-pinned={state.pinned || undefined}
                data-dragging={dragKey === state.key || undefined}
                draggable={!dragDisabled}
                onDragStart={(e) => {
                  setDragKey(state.key);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnter={() => dragTo(state.key)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragKey(null);
                }}
                onDragEnd={() => setDragKey(null)}
              >
                <span
                  className="phxlist-colpick-grip"
                  data-disabled={dragDisabled || undefined}
                  title={dragDisabled ? t('columns.dragDisabled') : t('columns.drag')}
                >
                  <Icon name="morev" size={11} />
                </span>
                <Checkbox
                  checked={state.visible}
                  disabled={state.pinned}
                  onCheckedChange={() => controller.toggleColumn(state.key)}
                />
                <span className="text-phx-md min-w-0 flex-1 truncate">{column.label}</span>
                <button
                  type="button"
                  className="phxlist-pinbtn"
                  data-on={state.pinned || undefined}
                  title={state.pinned ? t('columns.unpin') : t('columns.pin')}
                  onClick={() => controller.togglePinColumn(state.key)}
                >
                  <Icon name="pin" size={11} />
                </button>
              </div>
            );
          })}
          {shown.length === 0 ? (
            <div className="p-phx-4 text-phx-sm text-phx-text-faint">
              {t('columns.noMatch', { query })}
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Toolbar (anatomie 02) — vlevo vykonání dotazu (Vyhledat, Živě) a počty, vpravo rychlé
 * hledání, řazení, sloupce. Statistiky/export/nový záznam jsou volitelné moduly dalších fází.
 */
export function ListToolbar<TItem>({
  controller,
  columns,
  fulltextPlaceholder,
  hasStats,
  hasExport,
  exportFileName,
  onCreate,
  createLabel,
}: {
  controller: ListPageController<TItem>;
  columns: ListColumnDef<TItem>[];
  fulltextPlaceholder?: string;
  /** Šablona má definované statistiky → zobrazit přepínač souhrnného pruhu. */
  hasStats?: boolean;
  /** Šablona má `api.export` → zobrazit tlačítko Export. */
  hasExport?: boolean;
  /** Název staženého XLSX souboru (jinak výchozí z locale). */
  exportFileName?: string;
  /** Akce „nový záznam" — primární tlačítko na konci toolbaru (jen mimo picker režim). */
  onCreate?: () => void;
  /** Label tlačítka „nový záznam" (jinak obecný text). */
  createLabel?: string;
}) {
  const { t } = useTranslation('listPage');

  const columnsByKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);

  const handleExport = async () => {
    const exportColumns: ListExportColumn[] = controller.columnStates
      .filter((state) => state.visible)
      .map((state) => ({
        key: state.key,
        header: columnsByKey.get(state.key)?.label ?? state.key,
      }));
    const result = await controller.exportList(exportColumns);
    if (!result) {
      return;
    }
    if (result.trimmed) {
      toast({
        severity: 'info',
        description: t('toolbar.exportTrimmed', {
          max: NUMBER_FORMAT.format(LIST_EXPORT_MAX_ROWS),
        }),
      });
    }
    downloadBlob(result.blob, exportFileName ?? t('toolbar.exportFile'));
  };

  return (
    <div className="phxlist-toolbar">
      <div className="phxlist-toolbar-left">
        <button
          type="button"
          className="phxlist-btn"
          data-variant={controller.pending ? 'primary' : undefined}
          disabled={controller.isLoading}
          title={t('toolbar.searchTitle')}
          onClick={controller.search}
        >
          <span className={controller.isLoading ? 'phxlist-spin' : undefined}>
            <Icon name={controller.isLoading ? 'refresh' : 'search'} size={13} />
          </span>
          {t('toolbar.search')}
          {controller.pending ? (
            <span className="phxlist-draftdot" title={t('toolbar.pending')} />
          ) : null}
        </button>
        <Switch
          checked={controller.live}
          onCheckedChange={controller.setLive}
          label={t('toolbar.live')}
          title={t('toolbar.liveTitle')}
        />
        <span className="phxlist-vdivider" />
        <span className="text-phx-md text-phx-text-2">
          {controller.searched ? (
            <>
              <b className="text-phx-text tabular-nums">
                {NUMBER_FORMAT.format(controller.totalCount)}
              </b>{' '}
              {t('toolbar.records', { count: controller.totalCount })}
            </>
          ) : (
            <span className="text-phx-text-faint">{t('toolbar.notLoaded')}</span>
          )}
        </span>
        {controller.selection.size > 0 ? (
          <span className="phxlist-selcount">
            <b className="tabular-nums">{controller.selection.size}</b>{' '}
            {t('toolbar.selected', { count: controller.selection.size })}
          </span>
        ) : null}
      </div>
      <div className="phxlist-toolbar-right">
        <div className="w-[190px]">
          <Input
            size="sm"
            leadingIcon="search"
            placeholder={fulltextPlaceholder ?? t('toolbar.quickSearch')}
            value={controller.fulltext}
            onChange={(e) => controller.setFulltext(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                controller.search();
              }
            }}
          />
        </div>
        <span className="phxlist-vdivider" />
        {hasStats ? (
          <button
            type="button"
            className="phxlist-btn"
            data-active={controller.statsOpen || undefined}
            title={t('toolbar.statsTitle')}
            onClick={() => controller.setStatsOpen(!controller.statsOpen)}
          >
            <Icon name="trend" size={13} />
            {t('toolbar.stats')}
          </button>
        ) : null}
        {hasExport ? (
          <button
            type="button"
            className="phxlist-btn"
            disabled={!controller.searched}
            title={t('toolbar.exportTitle')}
            onClick={() => void handleExport()}
          >
            <Icon name="download" size={13} />
            {t('toolbar.export')}
          </button>
        ) : null}
        <SortMenu controller={controller} columns={columns} />
        <ColumnsPopover controller={controller} columns={columns} />
        {onCreate ? (
          <Button variant="primary" size="md" iconLeft="plus" onClick={onCreate}>
            {createLabel ?? t('toolbar.create')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
